import { useCallback, useEffect, useRef, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRoomContext,
} from "@livekit/components-react";
import { RoomEvent, type RemoteParticipant } from "livekit-client";
import { useLiveKitToken } from "@/hooks/useLiveKitToken";
import VideoGrid from "./VideoGrid";
import ControlBar from "./ControlBar";
import ChatPanel from "./ChatPanel";
import Whiteboard from "./Whiteboard";
import ParticipantsPanel, { type ParticipantStatus } from "./ParticipantsPanel";
import PollPanel from "./PollPanel";
import type { ChatItem, CollabMessage, ChatAttachment, Poll } from "./types";

interface LiveClassRoomProps {
  title: string;
  roomCode: string;
  userName: string;
  isHost: boolean;
  hostIdentity: string;
  onExit: () => void;
  onEnd?: () => void;
}

type PanelKind = "none" | "chat" | "whiteboard" | "participants" | "poll";

function nowTime(): string {
  return new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/* ---------- Loading / Error screens ---------- */
function ConnectingScreen({ title }: { title: string }) {
  return (
    <div className="fixed inset-0 z-50 bg-background-950 flex flex-col items-center justify-center gap-4">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-500/30">
        <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" aria-hidden="true"></i>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-background-200">{title}</p>
        <p className="text-xs text-background-500 mt-1">Canlı derse bağlanılıyor…</p>
      </div>
    </div>
  );
}

function ErrorScreen({ error, onExit }: { error: string | null; onExit: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-background-950 flex flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
        <i className="ri-error-warning-line text-2xl text-red-400" aria-hidden="true"></i>
      </div>
      <div>
        <p className="text-sm font-semibold text-background-200">Bağlantı kurulamadı</p>
        <p className="text-xs text-background-500 mt-1 max-w-sm">{error || "Canlı ders başlatılamadı."}</p>
      </div>
      <button
        type="button"
        onClick={onExit}
        className="h-10 px-5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
      >
        Geri Dön
      </button>
    </div>
  );
}

/* ---------- Exit confirmation dialog ---------- */
function ExitDialog({
  isHost,
  onConfirm,
  onCancel,
  onEnd,
}: {
  isHost: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onEnd?: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4">
      <div className="bg-background-900 rounded-2xl border border-background-700 p-6 max-w-sm w-full shadow-2xl">
        <h2 className="text-base font-bold text-background-50 mb-2">
          {isHost ? "Dersi bitirmek istiyor musunuz?" : "Dersten ayrılmak istiyor musunuz?"}
        </h2>
        <p className="text-sm text-background-400 mb-6 leading-relaxed">
          {isHost
            ? "Dersi bitirirseniz tüm katılımcılar odadan çıkarılır."
            : "Dersten ayrılırsanız ders devam eder, dilediğinizde tekrar katılabilirsiniz."}
        </p>
        <div className="flex flex-col gap-2">
          {isHost && onEnd && (
            <button
              type="button"
              onClick={onEnd}
              className="h-11 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              Dersi Bitir (Herkesi Çıkar)
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className="h-11 rounded-xl bg-background-700 text-background-100 text-sm font-semibold hover:bg-background-600 transition-colors whitespace-nowrap"
          >
            {isHost ? "Ben Ayrılayım (Ders Sürsün)" : "Dersten Ayrıl"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-11 rounded-xl border border-background-700 text-background-400 text-sm hover:bg-background-800 transition-colors whitespace-nowrap"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inner room content ---------- */
export function RoomContent({
  title,
  roomCode,
  isHost,
  hostIdentity,
  onExit,
  onEnd,
}: {
  title: string;
  roomCode: string;
  isHost: boolean;
  hostIdentity: string;
  onExit: () => void;
  onEnd?: () => void;
}) {
  const room = useRoomContext();
  const myIdentity = room.localParticipant.identity;
  const myName = room.localParticipant.name || myIdentity;

  // --- Chat state ---
  // Host sees all messages; student only sees their own + host's messages.
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [unreadChat, setUnreadChat] = useState(0);

  // --- Typing indicator ---
  const [typing, setTyping] = useState<Record<string, string>>({});

  // --- Hand raise ---
  const [raised, setRaised] = useState<Record<string, string>>({});

  // --- Participant list & permissions ---
  const [participants, setParticipants] = useState<ParticipantStatus[]>([]);
  // micAllowed / camAllowed per identity (host grants these)
  const [permissions, setPermissions] = useState<Record<string, { mic: boolean; cam: boolean }>>({});

  // For students: their own current permission (default: both allowed until host explicitly restricts)
  const [myMicAllowed, setMyMicAllowed] = useState(true);
  const [myCamAllowed, setMyCamAllowed] = useState(true);

  // --- Panel ---
  const [activePanel, setActivePanel] = useState<PanelKind>("none");
  const activePanelRef = useRef<PanelKind>("none");
  useEffect(() => { activePanelRef.current = activePanel; }, [activePanel]);

  // --- Timer ---
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // --- Exit dialog ---
  const [showExit, setShowExit] = useState(false);

  // --- Muted-by-host indicator map (for VideoGrid) ---
  const [mutedByHost, setMutedByHost] = useState<Record<string, { mic: boolean; cam: boolean }>>({});

  // --- Live poll (canlı anket) ---
  const [poll, setPoll] = useState<Poll | null>(null);
  const [myVote, setMyVote] = useState<number | null>(null);
  const pollRef = useRef<Poll | null>(null);
  useEffect(() => { pollRef.current = poll; }, [poll]);

  /* ------ Sync participant list from room ------ */
  const syncParticipants = useCallback(() => {
    const list: ParticipantStatus[] = [];
    // local participant
    list.push({
      identity: myIdentity,
      name: myName,
      micAllowed: permissions[myIdentity]?.mic ?? true,
      camAllowed: permissions[myIdentity]?.cam ?? true,
      handRaised: !!raised[myIdentity],
      connected: true,
    });
    room.remoteParticipants.forEach((p: RemoteParticipant) => {
      list.push({
        identity: p.identity,
        name: p.name || p.identity,
        micAllowed: permissions[p.identity]?.mic ?? true,
        camAllowed: permissions[p.identity]?.cam ?? true,
        handRaised: !!raised[p.identity],
        connected: true,
      });
    });
    setParticipants(list);
  }, [myIdentity, myName, permissions, raised, room]);

  useEffect(() => {
    syncParticipants();
    room.on(RoomEvent.ParticipantConnected, syncParticipants);
    room.on(RoomEvent.ParticipantDisconnected, syncParticipants);
    return () => {
      room.off(RoomEvent.ParticipantConnected, syncParticipants);
      room.off(RoomEvent.ParticipantDisconnected, syncParticipants);
    };
  }, [room, syncParticipants]);

  /* ------ Data channel handler ------ */
  const sendData = useCallback((msg: CollabMessage, targetIdentity?: string) => {
    const payload = new TextEncoder().encode(JSON.stringify(msg));
    if (targetIdentity) {
      const remoteP = room.remoteParticipants.get(targetIdentity);
      if (remoteP) {
        room.localParticipant.publishData(payload, { reliable: true, destinationIdentities: [targetIdentity] });
      }
    } else {
      room.localParticipant.publishData(payload, { reliable: true });
    }
  }, [room]);

  useEffect(() => {
    const handler = (payload: Uint8Array, participant?: RemoteParticipant) => {
      let msg: CollabMessage;
      try {
        msg = JSON.parse(new TextDecoder().decode(payload)) as CollabMessage;
      } catch {
        return;
      }
      const senderId = participant?.identity || msg.identity || "";
      const senderName = participant?.name || msg.name || senderId;
      const senderIsHost = senderId === hostIdentity;

      if (msg.kind === "chat") {
        // Students receive only messages where sender is host (private chat to host goes back only to that student via destination)
        // Host receives all student messages
        if (isHost || senderIsHost) {
          const item: ChatItem = {
            id: msg.messageId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: senderName,
            text: String(msg.text || ""),
            time: nowTime(),
            mine: false,
            attachment: msg.attachment,
          };
          setChat((prev) => [...prev, item]);
          if (activePanelRef.current !== "chat") {
            setUnreadChat((n) => n + 1);
          }
        }
      } else if (msg.kind === "typing") {
        // Only meaningful when the sender's messages are visible to us
        if (isHost || senderIsHost) {
          setTyping((prev) => {
            const next = { ...prev };
            if (msg.isTyping) {
              next[senderId] = senderName;
            } else {
              delete next[senderId];
            }
            return next;
          });
        }
      } else if (msg.kind === "reaction") {
        const targetId = msg.targetMessageId;
        if (targetId) {
          // Update reaction on the matching message
          setChat((prev) =>
            prev.map((c) => (c.id === targetId ? { ...c, reaction: msg.reaction } : c))
          );
        }
      } else if (msg.kind === "raise") {
        setRaised((prev) => ({ ...prev, [senderId]: senderName }));
      } else if (msg.kind === "lower") {
        setRaised((prev) => {
          const next = { ...prev };
          delete next[senderId];
          return next;
        });
      } else if (msg.kind === "permission") {
        // This arrives at the target student
        const mic = msg.mic ?? true;
        const cam = msg.cam ?? true;
        setMyMicAllowed(mic);
        setMyCamAllowed(cam);
        // Update muted-by-host visual map
        setMutedByHost((prev) => ({ ...prev, [senderId]: { mic, cam } }));
      } else if (msg.kind === "poll") {
        if (msg.pollId && msg.question && Array.isArray(msg.options)) {
          setPoll({ id: msg.pollId, question: msg.question, options: msg.options, votes: msg.options.map(() => 0) });
          setMyVote(null);
        }
      } else if (msg.kind === "poll_vote") {
        if (isHost && pollRef.current && msg.pollId === pollRef.current.id) {
          const idx = msg.optionIndex ?? -1;
          if (idx >= 0) {
            setPoll((prev) =>
              prev ? { ...prev, votes: prev.votes.map((v, i) => (i === idx ? v + 1 : v)) } : prev,
            );
          }
        }
      }
    };

    room.on(RoomEvent.DataReceived, handler);
    return () => {
      room.off(RoomEvent.DataReceived, handler);
    };
  }, [room, isHost, hostIdentity]);

  /* ------ Sync raised to participant list ------ */
  useEffect(() => {
    syncParticipants();
  }, [raised, permissions, syncParticipants]);

  /* ------ Chat send ------ */
  const sendChat = useCallback((text: string) => {
    const t = text.trim();
    if (!t) return;
    const messageId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const msg: CollabMessage = { kind: "chat", name: myName, text: t, identity: myIdentity, messageId };

    if (isHost) {
      // Host broadcasts to all
      sendData(msg);
    } else {
      // Student sends only to host
      sendData(msg, hostIdentity);
    }

    setChat((prev) => [
      ...prev,
      {
        id: messageId,
        name: myName,
        text: t,
        time: nowTime(),
        mine: true,
      },
    ]);
  }, [isHost, myName, myIdentity, hostIdentity, sendData]);

  /* ------ Attachment send ------ */
  const sendAttachment = useCallback((attachment: ChatAttachment) => {
    const messageId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const msg: CollabMessage = { kind: "chat", name: myName, text: "", identity: myIdentity, attachment, messageId };

    if (isHost) {
      sendData(msg);
    } else {
      sendData(msg, hostIdentity);
    }

    setChat((prev) => [
      ...prev,
      {
        id: messageId,
        name: myName,
        text: "",
        time: nowTime(),
        mine: true,
        attachment,
      },
    ]);
  }, [isHost, myName, myIdentity, hostIdentity, sendData]);

  /* ------ Reaction send ------ */
  const sendReaction = useCallback((messageId: string, emoji: string) => {
    // Optimistically update local state
    setChat((prev) => prev.map((c) => (c.id === messageId ? { ...c, reaction: emoji } : c)));

    const msg: CollabMessage = {
      kind: "reaction",
      name: myName,
      identity: myIdentity,
      targetMessageId: messageId,
      reaction: emoji,
    };
    if (isHost) {
      sendData(msg);
    } else {
      sendData(msg, hostIdentity);
    }
  }, [isHost, myName, myIdentity, hostIdentity, sendData]);

  /* ------ Typing send ------ */
  const sendTyping = useCallback((isTyping: boolean) => {
    const msg: CollabMessage = { kind: "typing", name: myName, identity: myIdentity, isTyping };
    if (isHost) {
      sendData(msg);
    } else {
      sendData(msg, hostIdentity);
    }
  }, [isHost, myName, myIdentity, hostIdentity, sendData]);

  /* ------ Hand raise ------ */
  const myRaised = !!raised[myIdentity];
  const toggleRaise = useCallback(() => {
    if (myRaised) {
      sendData({ kind: "lower", identity: myIdentity, name: myName });
      setRaised((prev) => {
        const next = { ...prev };
        delete next[myIdentity];
        return next;
      });
    } else {
      sendData({ kind: "raise", identity: myIdentity, name: myName });
      setRaised((prev) => ({ ...prev, [myIdentity]: myName }));
    }
  }, [myRaised, myIdentity, myName, sendData]);

  /* ------ Live poll (canlı anket) ------ */
  const createPoll = useCallback((question: string, options: string[]) => {
    const pollId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sendData({ kind: "poll", pollId, question, options, identity: myIdentity, name: myName });
    setPoll({ id: pollId, question, options, votes: options.map(() => 0) });
    setMyVote(null);
  }, [sendData, myIdentity, myName]);

  const votePoll = useCallback((optionIndex: number) => {
    if (myVote !== null || !poll) return;
    setMyVote(optionIndex);
    if (isHost) {
      setPoll((prev) => (prev ? { ...prev, votes: prev.votes.map((v, i) => (i === optionIndex ? v + 1 : v)) } : prev));
    } else {
      sendData({ kind: "poll_vote", pollId: poll.id, optionIndex, identity: myIdentity, name: myName }, hostIdentity);
    }
  }, [myVote, poll, isHost, sendData, myIdentity, myName, hostIdentity]);

  /* ------ Host permission grants ------ */
  const handleGrantMic = useCallback((identity: string, allow: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [identity]: { ...(prev[identity] ?? { mic: true, cam: true }), mic: allow },
    }));
    setMutedByHost((prev) => ({
      ...prev,
      [identity]: { ...(prev[identity] ?? { mic: true, cam: true }), mic: allow },
    }));
    sendData({ kind: "permission", identity, mic: allow, cam: permissions[identity]?.cam ?? true }, identity);
  }, [permissions, sendData]);

  const handleGrantCam = useCallback((identity: string, allow: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [identity]: { ...(prev[identity] ?? { mic: true, cam: true }), cam: allow },
    }));
    setMutedByHost((prev) => ({
      ...prev,
      [identity]: { ...(prev[identity] ?? { mic: true, cam: true }), cam: allow },
    }));
    sendData({ kind: "permission", identity, mic: permissions[identity]?.mic ?? true, cam: allow }, identity);
  }, [permissions, sendData]);

  const handleLowerHand = useCallback((identity: string) => {
    setRaised((prev) => {
      const next = { ...prev };
      delete next[identity];
      return next;
    });
    // signal the student to lower their hand indicator
    sendData({ kind: "lower", identity }, identity);
  }, [sendData]);

  /* ------ Panel toggle ------ */
  const togglePanel = useCallback((panel: "chat" | "whiteboard" | "participants" | "poll") => {
    setActivePanel((prev) => {
      const next = prev === panel ? "none" : panel;
      if (next === "chat") setUnreadChat(0);
      return next;
    });
  }, []);

  const raisedNames = Object.values(raised);
  const raisedSet = new Set(Object.keys(raised));
  const typingUsers = Object.values(typing);

  const participantCount = 1 + room.remoteParticipants.size;

  return (
    <>
      {showExit && (
        <ExitDialog
          isHost={isHost}
          onConfirm={onExit}
          onCancel={() => setShowExit(false)}
          onEnd={onEnd}
        />
      )}

      <div className="w-full h-full flex flex-col">
        {/* ===== Top bar ===== */}
        <div className="h-14 bg-background-900 border-b border-background-700/50 flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setShowExit(true)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-background-200 hover:bg-background-700 transition-colors shrink-0"
              aria-label="Çıkış"
            >
              <i className="ri-arrow-left-line text-lg" aria-hidden="true"></i>
            </button>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-background-50 truncate leading-tight">{title}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-background-500">Oda: {roomCode}</span>
                <span className="text-background-700">·</span>
                <span className="text-[11px] text-background-500 flex items-center gap-1">
                  <i className="ri-group-line text-[10px]" aria-hidden="true"></i>
                  {participantCount} katılımcı
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Raised hands indicator */}
            {raisedNames.length > 0 && (
              <button
                type="button"
                onClick={() => isHost && togglePanel("participants")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/30 text-accent-400 text-xs font-semibold animate-pulse"
              >
                <i className="fa-solid fa-hand text-sm" aria-hidden="true"></i>
                {raisedNames.length} el kaldırdı
              </button>
            )}

            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" aria-hidden="true"></span>
              <span className="text-xs font-bold text-red-400">CANLI</span>
            </div>

            {/* Host badge */}
            {isHost && (
              <span className="px-2.5 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-semibold border border-primary-500/30">
                Öğretmen
              </span>
            )}

            <button
              type="button"
              onClick={() => setShowExit(true)}
              className="h-9 px-4 rounded-lg bg-red-600/90 text-white text-xs font-bold hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              {isHost ? "Dersi Bitir" : "Ayrıl"}
            </button>
          </div>
        </div>

        {/* ===== Main content ===== */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video area */}
          <div className="flex-1 flex flex-col overflow-hidden relative bg-background-950">
            <div className="flex-1 overflow-y-auto p-4">
              <VideoGrid
                hostIdentity={hostIdentity}
                raisedIdentities={raisedSet}
                mutedByHost={mutedByHost}
              />
            </div>
          </div>

          {/* Right panels */}
          {activePanel === "chat" && (
            <ChatPanel
              messages={chat}
              onSend={sendChat}
              onSendAttachment={sendAttachment}
              onReact={sendReaction}
              onTyping={sendTyping}
              typingUsers={typingUsers}
              onClose={() => setActivePanel("none")}
              isHost={isHost}
              title={title}
            />
          )}
          {activePanel === "whiteboard" && (
            <Whiteboard onClose={() => setActivePanel("none")} />
          )}
          {activePanel === "participants" && (
            <ParticipantsPanel
              isHost={isHost}
              hostIdentity={hostIdentity}
              participants={participants}
              onGrantMic={handleGrantMic}
              onGrantCam={handleGrantCam}
              onLowerHand={handleLowerHand}
              onClose={() => setActivePanel("none")}
            />
          )}
          {activePanel === "poll" && (
            <PollPanel
              isHost={isHost}
              poll={poll}
              myVote={myVote}
              onVote={votePoll}
              onCreate={createPoll}
              onClose={() => setActivePanel("none")}
            />
          )}
        </div>

        {/* ===== Bottom control bar ===== */}
        <ControlBar
          isHost={isHost}
          myRaised={myRaised}
          raisedCount={raisedNames.length}
          onToggleRaise={toggleRaise}
          activePanel={activePanel}
          onTogglePanel={togglePanel}
          unreadChat={unreadChat}
          participantCount={participantCount}
          micAllowed={myMicAllowed}
          camAllowed={myCamAllowed}
          elapsedSeconds={elapsedSeconds}
        />
      </div>
    </>
  );
}

/* ---------- Root export ---------- */
export default function LiveClassRoom({
  title,
  roomCode,
  userName,
  isHost,
  hostIdentity,
  onExit,
  onEnd,
}: LiveClassRoomProps) {
  const { token, url, loading, error } = useLiveKitToken(roomCode, userName);

  if (loading) return <ConnectingScreen title={title} />;
  if (error || !token || !url) return <ErrorScreen error={error} onExit={onExit} />;

  return (
    <div className="fixed inset-0 z-50 bg-background-950">
      <LiveKitRoom
        serverUrl={url}
        token={token}
        connect
        audio
        video
        onDisconnected={onExit}
        className="w-full h-full"
      >
        <RoomContent
          title={title}
          roomCode={roomCode}
          isHost={isHost}
          hostIdentity={hostIdentity}
          onExit={onExit}
          onEnd={onEnd}
        />
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
}
